export type OnboardingV13StepId = 'welcome' | 'connect' | 'plan';

export interface OnboardingV13StepMeta {
    id: OnboardingV13StepId;
    title: string;
}

export interface OnboardingDataDefaults {
    workspaceId?: string;
    currentStep: number;
    industry: string;
    otherIndustry: string;
    businessName: string;
    logo: null;
    logoUrl: null;
    connectedStoreName: string;
    connectedStoreLogoUrl: string | null;
    connectedStoreCount: number;
    connectedIntegrations: string[];
    connectedIntegrationTrialRequirements: Record<string, 'checkout_required'>;
    trialEligibilityState: string | null;
    storeConnectionStoreUrl: string;
    storeConnectionStoreHash: string;
    storeConnectionAccountUuid: string;
    intent: string;
    otherIntent: string;
    plan: string;
    billingCycle: string;
    isTrialStarted: boolean;
    resumePlanAfterCheckout: boolean;
}

export interface InitialOnboardingWorkspacePayloadInput {
    timezone: string;
}

export interface OnboardingWorkspaceUpdatePayloadInput {
    storeName?: string | null;
    storeLogoUrl?: string | null;
    currentSettings?: Record<string, any> | null;
    intent?: string | null;
    otherIntent?: string | null;
}

export interface IntentSubmissionWorkspaceStateInput {
    workspaceId?: string | null;
    prepareWorkspaceError?: string | null;
}

export type OnboardingTrialActivationAction =
    | 'redirect-to-card-setup'
    | 'start-paid-checkout'
    | 'activated'
    | 'failed';

export interface OnboardingTrialActivationResponseLike {
    activationMode?: string | null;
    paymentUrl?: string | null;
    workspaceId?: string | null;
}

const CARD_SETUP_REQUIRED_ACTIVATION_MODES = new Set([
    'setup_card_required',
    'trial_checkout_required',
    'direct_card_required',
]);

export const resolveOnboardingTrialActivationAction = ({
    isPluginOnboarding,
    response,
}: {
    isPluginOnboarding: boolean;
    response?: OnboardingTrialActivationResponseLike | null;
}): OnboardingTrialActivationAction => {
    const activationMode = response?.activationMode?.trim() || '';
    const hasPaymentUrl = Boolean(response?.paymentUrl?.trim());
    const hasWorkspaceId = Boolean(response?.workspaceId?.trim());

    if (CARD_SETUP_REQUIRED_ACTIVATION_MODES.has(activationMode)) {
        return hasPaymentUrl ? 'redirect-to-card-setup' : 'failed';
    }

    if (activationMode === 'checkout_required') {
        return hasPaymentUrl ? 'redirect-to-card-setup' : 'start-paid-checkout';
    }

    if (isPluginOnboarding) {
        return activationMode === 'trial_activated' && hasWorkspaceId
            ? 'activated'
            : 'failed';
    }

    return hasWorkspaceId ? 'activated' : 'failed';
};

interface WorkspaceIntegrationMappingLike {
    store_name?: string | null;
    shop_name?: string | null;
    account_name?: string | null;
    logo_url?: string | null;
}

interface WorkspaceIntegrationLike {
    provider?: string | null;
    connected?: boolean;
    profile_data?: Record<string, any> | null;
    mappings?: WorkspaceIntegrationMappingLike[] | null;
}

export const ONBOARDING_V13_STEPS: OnboardingV13StepMeta[] = [
    { id: 'welcome', title: 'Welcome aboard, Bustly.' },
    { id: 'connect', title: 'Bring Bustly into the store.' },
    { id: 'plan', title: 'Start with a 7-day trial' },
];

const normalizeWorkspaceValue = (value?: string | null): string => (value || '').trim();

export const createInitialOnboardingData = (): OnboardingDataDefaults => ({
    workspaceId: undefined,
    currentStep: 0,
    industry: '',
    otherIndustry: '',
    businessName: '',
    logo: null,
    logoUrl: null,
    connectedStoreName: '',
    connectedStoreLogoUrl: null,
    connectedStoreCount: 0,
    connectedIntegrations: [],
    connectedIntegrationTrialRequirements: {},
    trialEligibilityState: null,
    storeConnectionStoreUrl: '',
    storeConnectionStoreHash: '',
    storeConnectionAccountUuid: '',
    intent: '',
    otherIntent: '',
    plan: 'Basic',
    billingCycle: 'monthly',
    isTrialStarted: false,
    resumePlanAfterCheckout: false,
});

export const clampOnboardingCurrentStep = (
    currentStep?: number | null,
): number => {
    const normalizedStep = Number(currentStep);

    if (!Number.isFinite(normalizedStep)) {
        return 0;
    }

    return Math.min(
        Math.max(Math.trunc(normalizedStep), 0),
        ONBOARDING_V13_STEPS.length - 1,
    );
};

export const getVisibleOnboardingSteps = (
    skipStoreConnectStep: boolean,
): OnboardingV13StepMeta[] => {
    if (!skipStoreConnectStep) {
        return ONBOARDING_V13_STEPS;
    }

    return ONBOARDING_V13_STEPS.filter((step) => step.id !== 'connect');
};

export const getVisibleOnboardingStepIndex = (
    currentStep: number,
    skipStoreConnectStep: boolean,
): number => {
    if (!skipStoreConnectStep || currentStep <= 0) {
        return currentStep;
    }

    return currentStep > 1 ? currentStep - 1 : 0;
};

export const resolveIntentSubmissionWorkspaceState = ({
    workspaceId,
    prepareWorkspaceError,
}: IntentSubmissionWorkspaceStateInput): 'ready' | 'wait' | 'error' => {
    if (normalizeWorkspaceValue(workspaceId)) {
        return 'ready';
    }

    if (!normalizeWorkspaceValue(prepareWorkspaceError)) {
        return 'wait';
    }

    return 'error';
};

const FREE_TRIAL_UNAVAILABLE_RESTRICTION_STATES = new Set([
    'already_bound',
    'trial_valid',
    'trial_expired',
]);

const isTrialResumeAllowed = (restrictionState?: string | null): boolean => (
    restrictionState === 'trial_resume_allowed'
);

export const updateConnectedIntegrationTrialRequirements = (
    currentRequirements: Record<string, 'checkout_required'> | null | undefined,
    platformId: string,
    options?: {
        restrictionState?: string | null;
        blockConnect?: boolean;
        allowsFreshTrial?: boolean;
        isPluginOnboarding?: boolean;
    },
): Record<string, 'checkout_required'> => {
    const nextRequirements = { ...(currentRequirements || {}) };

    if (!isTrialResumeAllowed(options?.restrictionState) && (
        (options?.restrictionState
            && FREE_TRIAL_UNAVAILABLE_RESTRICTION_STATES.has(
                options.restrictionState,
            )) ||
        options?.allowsFreshTrial === false
    )) {
        nextRequirements[platformId] = 'checkout_required';
        return nextRequirements;
    }

    delete nextRequirements[platformId];
    return nextRequirements;
};

export interface OnboardingTrialEligibilityConnection {
    id: string;
    isValidConnection: boolean;
    restrictionState?: string | null;
    allowsFreshTrial?: boolean | null;
}

export const isOnboardingTrialUnavailableConnection = (
    connection: OnboardingTrialEligibilityConnection,
): boolean => {
    if (!connection.isValidConnection) {
        return false;
    }

    return !isTrialResumeAllowed(connection.restrictionState)
        && (connection.allowsFreshTrial === false
        || FREE_TRIAL_UNAVAILABLE_RESTRICTION_STATES.has(
            connection.restrictionState || '',
        ));
};

/**
 * The free-trial rule is evaluated for each connected account/store. A single
 * provider can have several accounts, so provider names must never be used as
 * the persisted eligibility key.
 */
export const resolveConnectedIntegrationTrialRequirements = (
    connections: readonly OnboardingTrialEligibilityConnection[],
): Record<string, 'checkout_required'> => connections.reduce<Record<string, 'checkout_required'>>(
    (requirements, connection) => {
        if (isOnboardingTrialUnavailableConnection(connection)) {
            requirements[connection.id] = 'checkout_required';
        }
        return requirements;
    },
    {},
);

export const removeConnectedIntegrationTrialRequirement = (
    currentRequirements: Record<string, 'checkout_required'> | null | undefined,
    platformId: string,
): Record<string, 'checkout_required'> => {
    const nextRequirements = { ...(currentRequirements || {}) };
    delete nextRequirements[platformId];
    return nextRequirements;
};

export const resolveAggregatedOnboardingTrialEligibilityState = (
    currentRequirements: Record<string, 'checkout_required'> | null | undefined,
): string | null => {
    return Object.keys(currentRequirements || {}).length > 0 ? 'checkout_required' : null;
};

export const hasReusableOnboardingWorkspace = (
    workspaceId?: string | null,
    workspaces?: Array<{ id?: string | null }> | null,
): boolean => {
    const normalizedWorkspaceId = workspaceId?.trim();

    if (!normalizedWorkspaceId) {
        return false;
    }

    return Boolean(workspaces?.some((workspace) => workspace?.id === normalizedWorkspaceId));
};

export const buildInitialOnboardingWorkspacePayload = ({
    timezone,
}: InitialOnboardingWorkspacePayloadInput): {
    name: string;
    timezone: string;
} => {
    return {
        name: 'My Workspace',
        timezone,
    };
};

export const buildOnboardingWorkspaceUpdatePayload = ({
    storeName,
    storeLogoUrl,
    currentSettings,
    intent,
    otherIntent,
}: OnboardingWorkspaceUpdatePayloadInput): {
    name?: string;
    logoUrl?: string;
    settings: Record<string, any>;
} => {
    const normalizedStoreName = normalizeWorkspaceValue(storeName);
    const normalizedStoreLogoUrl = normalizeWorkspaceValue(storeLogoUrl);
    const normalizedIntent = normalizeWorkspaceValue(intent);
    const normalizedOtherIntent = (otherIntent || '').trim();
    const nextSettings: Record<string, any> = {
        ...(currentSettings || {}),
    };

    if (normalizedIntent) {
        nextSettings.intent = normalizedIntent;
    }

    if (normalizedOtherIntent) {
        nextSettings.otherIntent = normalizedOtherIntent;
    } else {
        delete nextSettings.otherIntent;
    }

    return {
        ...(normalizedStoreName ? { name: normalizedStoreName } : {}),
        ...(normalizedStoreLogoUrl ? { logoUrl: normalizedStoreLogoUrl } : {}),
        settings: nextSettings,
    };
};

export const resolveConnectedStoreProfile = (
    integrations: WorkspaceIntegrationLike[] = [],
    provider?: string | null,
): { storeName: string; storeLogoUrl: string | null } => {
    const normalizedProvider = normalizeWorkspaceValue(provider).toLowerCase();
    const integration = integrations.find((item) => {
        if (!item?.connected) {
            return false;
        }

        if (!normalizedProvider) {
            return true;
        }

        return normalizeWorkspaceValue(item.provider).toLowerCase() === normalizedProvider;
    });

    if (!integration) {
        return {
            storeName: '',
            storeLogoUrl: null,
        };
    }

    const mappings = integration.mappings || [];
    const primaryMapping = mappings[0] || {};
    const profileData = integration.profile_data || {};

    return {
        storeName:
            normalizeWorkspaceValue(primaryMapping.store_name)
            || normalizeWorkspaceValue(primaryMapping.shop_name)
            || normalizeWorkspaceValue(primaryMapping.account_name)
            || normalizeWorkspaceValue(profileData.store_name)
            || normalizeWorkspaceValue(profileData.shop_name)
            || normalizeWorkspaceValue(profileData.account_name),
        storeLogoUrl:
            normalizeWorkspaceValue(primaryMapping.logo_url)
            || normalizeWorkspaceValue(profileData.logo_url)
            || null,
    };
};
